import { useEffect, useRef, type RefObject } from "react";

export type UnaricPaypageMessageHandlers = {
  onSuccessScreen?: () => void;
  onErrorWithMessage?: (message: string) => void;
  onErrorScreen?: () => void;
  onErrorFlowClosed?: () => void;
  onComplete?: () => void;
  onCancel?: () => void;
  onProcessing?: () => void;
  onPostcodeDialog?: () => void;
  onDirectDebitDialog?: () => void;
};

export type UseUnaricPaypagePostMessageOptions = {
  iframeSrc: string | null;
  enabled: boolean;
  handlers: UnaricPaypageMessageHandlers;
  paymentIframeRef?: RefObject<HTMLIFrameElement | null>;
};

function isMessageFromPaymentIframe(
  event: MessageEvent,
  paymentIframeRef: RefObject<HTMLIFrameElement | null>
): boolean {
  const iframeWindow = paymentIframeRef.current?.contentWindow;
  if (iframeWindow == null) {
    return false;
  }
  return event.source === iframeWindow;
}

// Unaric prefixes error text with ":" — strip before display.
export function normalizeUnaricPaypageErrorMessage(raw: string): string {
  const trimmed = raw.trim().replace(/^:\s*/, "");
  if (!trimmed) return trimmed;
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
}

const PREFIX = {
  EXIT_SCREEN: "asp--exit-screen",
  ERROR_VALUE: "asp--error--value",
  ERROR_SCREEN: "asp--error-screen",
  ERROR: "asp--error",
  COMPLETE: "asp--complete",
  CANCEL: "asp--cancel",
  PROCESSING: "asp--processing",
  PCDIALOG: "asp--pcdialog",
  DDDIALOG: "asp--dddialog"
} as const;

export function routeUnaricPaypageMessage(
  rawData: string,
  handlers: UnaricPaypageMessageHandlers
): void {
  const d = rawData.trim();
  if (d.length === 0) return;

  if (d.startsWith(PREFIX.ERROR_VALUE)) {
    handlers.onErrorWithMessage?.(
      normalizeUnaricPaypageErrorMessage(d.slice(PREFIX.ERROR_VALUE.length))
    );
    return;
  }
  if (d === PREFIX.ERROR_SCREEN) {
    handlers.onErrorScreen?.();
    return;
  }
  if (d === PREFIX.ERROR) {
    handlers.onErrorFlowClosed?.();
    return;
  }
  if (d === PREFIX.EXIT_SCREEN) {
    handlers.onSuccessScreen?.();
    return;
  }
  if (d === PREFIX.COMPLETE) {
    handlers.onComplete?.();
    return;
  }
  if (d === PREFIX.CANCEL) {
    handlers.onCancel?.();
    return;
  }
  if (d === PREFIX.PROCESSING) {
    handlers.onProcessing?.();
    return;
  }
  if (d === PREFIX.PCDIALOG) {
    handlers.onPostcodeDialog?.();
    return;
  }
  if (d === PREFIX.DDDIALOG) {
    handlers.onDirectDebitDialog?.();
    return;
  }
}

export function useUnaricPaypagePostMessage(
  options: UseUnaricPaypagePostMessageOptions
): void {
  const { iframeSrc, enabled, handlers, paymentIframeRef } = options;
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;
  const paymentIframeRefRef = useRef(paymentIframeRef);
  paymentIframeRefRef.current = paymentIframeRef;

  useEffect(() => {
    if (!enabled || !iframeSrc) return;

    let expectedOrigin: string;
    try {
      expectedOrigin = new URL(iframeSrc).origin;
    } catch {
      return;
    }

    const onMessage = (event: MessageEvent) => {
      if (event.source == null) return;
      if (event.origin !== expectedOrigin) return;
      const iframeRef = paymentIframeRefRef.current;
      if (
        iframeRef &&
        !isMessageFromPaymentIframe(event, iframeRef)
      ) {
        return;
      }
      if (typeof event.data !== "string") return;
      routeUnaricPaypageMessage(event.data, handlersRef.current);
    };

    globalThis.addEventListener("message", onMessage);

    return () => {
      globalThis.removeEventListener("message", onMessage);
    };
  }, [enabled, iframeSrc]);
}
